import { apiClient, pcMilerClient } from '@api/apiClient';
import { V4ApiResponse } from '@api/types';
import { Token } from '@common/types';
import { ToastType } from '@components/Toast';
import { getDB } from '@database';
import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
} from '@microsoft/signalr';
import { MessagePackHubProtocol } from '@microsoft/signalr-protocol-msgpack';
import { HoldsInfo } from '@store/holdsTab/types';
import { OldLoad, OldLoadPayload } from '@store/loadboard/oldLoads/types';
import { CreateAnnouncementRequest } from '@typeDefinitions/messagePayload';
import { ReleaseNoteType } from '@typeDefinitions/releaseNotesTypes';
import { CACHE_DURATION, fetchWithCache } from '@utils/cacheUtils';
import { logError } from '@utils/errorLogging';
import { createError } from '@utils/errorUtils';
import { routeLoginPage } from '@utils/routeLoginPage';
import { handleToast, printError, printLog } from '@utils/utils';
import size from 'lodash/size';

import {
  AllApprovalsTypes,
  AnnouncementMessagesType,
  AvailablePosting,
  CheckRequestType,
  CreditIncreaseRequestCustomer,
  DoNotSolicitRequest,
  NeedPaperworkTypes,
  NotificationTypes,
  PcMilerApiResponse,
  PendingApprovals,
  PendingChecksTypes,
  PendingCustomers,
} from './types';

export interface SignalrHubProps {
  connection: string;
  loginUrl: string;
  preferWebSockets?: boolean;
  transportMode?: 'auto' | 'websocket' | 'sse';
}

// Add the import statement for SignalrHubProps

export async function createSocket(
  props: SignalrHubProps,
): Promise<HubConnection> {
  const tokenResponse = await fetch(props.loginUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const token: Token = (await tokenResponse.json()) as Token;
  if (!token.token) {
    routeLoginPage();
  }
  const buildConnection = (
    webSocketOnly: boolean,
    includeWebSocketInNegotiated = true,
  ) => {
    return new HubConnectionBuilder()
      .withUrl(props.connection, {
        skipNegotiation: webSocketOnly,
        transport: webSocketOnly
          ? HttpTransportType.WebSockets
          : includeWebSocketInNegotiated
            ? HttpTransportType.WebSockets |
              HttpTransportType.ServerSentEvents |
              HttpTransportType.LongPolling
            : HttpTransportType.ServerSentEvents |
              HttpTransportType.LongPolling,
        accessTokenFactory: () => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return token.token;
        },
      })
      .withHubProtocol(new MessagePackHubProtocol())
      .withAutomaticReconnect()
      .build();
  };

  const shouldTryWebSocketsFirst = props.preferWebSockets ?? true;
  const transportMode = props.transportMode ?? 'auto';

  if (transportMode === 'websocket') {
    const wsOnlyConnection = buildConnection(true);
    await wsOnlyConnection.start();
    printLog('SignalR connected using WebSockets-only mode');
    return wsOnlyConnection;
  }

  if (transportMode === 'sse') {
    const negotiatedConnection = buildConnection(false, false);
    await negotiatedConnection.start();
    printLog('SignalR negotiated transport connected without WebSockets');
    return negotiatedConnection;
  }

  if (!shouldTryWebSocketsFirst) {
    const negotiatedConnection = buildConnection(false, false);
    await negotiatedConnection.start();
    printLog('SignalR negotiated transport connected without WebSockets');
    return negotiatedConnection;
  }

  const wsConnection = buildConnection(true);

  try {
    await wsConnection.start();
    return wsConnection;
  } catch (err) {
    printError(
      'SignalR WebSocket-only startup failed, retrying with negotiated transport without WebSockets',
      err,
    );

    const fallbackConnection = buildConnection(false, false);
    try {
      await fallbackConnection.start();
      printLog('SignalR fallback transport connected successfully');
      return fallbackConnection;
    } catch (fallbackErr) {
      printError('SignalR fallback startup failed', fallbackErr);
      throw fallbackErr;
    }
  }
}

export const IsFeatureFlagEnabled = async (
  guid: string,
): Promise<boolean | undefined> => {
  try {
    const result = (await apiClient.get(
      `FeatureFlag/GetIsFeatureEnabledV2/${guid}`,
    )) as unknown as boolean;
    return result;
  } catch (e) {
    logError('IsFeatureFlagEnabledByName', e as Error);
    return undefined;
  }
};

export const getEffectiveIsEnabledForUserByFeatureName = async (
  name: string,
): Promise<boolean | undefined> => {
  try {
    const result = (await apiClient.get(
      `FeatureFlag/GetEffectiveIsEnabledForUserByFeatureName/${name}`,
    )) as unknown as boolean;
    return result;
  } catch (e) {
    logError('getEffectiveIsEnabledForUserByFeatureName', e as Error);
    return false;
  }
};

export const fetchAllAnnouncement = async () => {
  try {
    const response: V4ApiResponse<AnnouncementMessagesType[]> =
      await apiClient.get('/Announcement?top=20');
    return response;
  } catch (e: unknown) {
    const error = createError(e, 'Failed to get Announcement');
    handleToast(ToastType.Error, error.message);
  }
};

type AnnouncementDocumentUpload = {
  url: string;
  name: string;
  documentId?: number;
  rawUrl?: string;
  thumbnailUrl?: string;
  mimeType?: string;
  fileSize?: number;
  width?: number;
  height?: number;
  altText?: string;
};

export const uploadAnnouncementDocument = async (
  file: File,
  entity: { entityId: number; documentEntityType: number },
): Promise<AnnouncementDocumentUpload | null> => {
  try {
    const formData = new FormData();
    formData.append('Files', file);
    formData.append('entityId', String(entity.entityId));
    formData.append('documentEntityType', String(entity.documentEntityType));
    const response: V4ApiResponse<{
      results: AnnouncementDocumentUpload[];
      uploadErrors: { message?: string }[];
    }> = await apiClient.post('/document/CreateDocument', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    const uploaded = response.value.results[0];
    if (response.isSuccess && uploaded.url) {
      return uploaded;
    }
    const uploadError = response.value.uploadErrors[0]?.message;
    handleToast(
      ToastType.Error,
      uploadError ?? `Failed to upload ${file.name}`,
    );
    return null;
  } catch (e: unknown) {
    const error = createError(e, `Failed to upload ${file.name}`);
    handleToast(ToastType.Error, error.message);
    return null;
  }
};

export const addAnnouncement = async (
  payload: CreateAnnouncementRequest,
): Promise<number | null> => {
  try {
    const response: V4ApiResponse<number | { id?: number }> =
      await apiClient.post('/Announcement', payload);
    const createdId =
      typeof response.value === 'number' ? response.value : response.value.id;
    if (response.isSuccess && typeof createdId === 'number') {
      handleToast(ToastType.Success, 'Announcement was broadcast');
      return createdId;
    }
    return null;
  } catch (e: unknown) {
    const error = createError(e, 'Failed to broadcast Announcement');
    handleToast(ToastType.Error, error.message);
    return null;
  }
};

export const sendDirectMessage = async (
  recipientUserId: number,
  payload: CreateAnnouncementRequest['payload'],
): Promise<boolean> => {
  try {
    if (!payload) {
      throw new Error('Message content is required');
    }

    const response: V4ApiResponse<null> = await apiClient.post(
      '/Notification/direct-message',
      { recipientUserId, payload },
    );
    if (response.isSuccess) {
      return true;
    }
    throw new Error(response.errors[0]?.message ?? 'Failed to send message');
  } catch (error: unknown) {
    logError('sendDirectMessage', error as Error);
    throw createError(error, 'Failed to send message');
  }
};

export const announcementMarkAsRead = async (payload: number[]) => {
  try {
    const response: { isSuccess: boolean } = await apiClient.post(
      '/Announcement/markasread',
      payload,
    );
    if (response.isSuccess) {
      handleToast(ToastType.Success, 'Announcement marked as read');
    }
  } catch (e: unknown) {
    const error = createError(e, 'Failed to mark as read');
    handleToast(ToastType.Error, error.message);
  }
};

export const fetchCreditIncreaseRequestCustomers = async () => {
  try {
    const response: V4ApiResponse<CreditIncreaseRequestCustomer[]> =
      await apiClient.get('queue/creditincrease');
    if (response.isSuccess) {
      return response.value;
    }
  } catch (error) {
    if (error instanceof Error) {
      handleToast(ToastType.Error, 'Failed to get Credit Increase Request');
    }
  }
};

export const claimCreditIncreaseRequest = async (payload: {
  id: number;
  claim: boolean;
  comment?: string;
}): Promise<boolean> => {
  try {
    const response = (await apiClient.post(
      'queue/creditincrease/claim',
      payload,
    )) as V4ApiResponse<unknown>;
    if (response.isSuccess) {
      handleToast(
        ToastType.Success,
        payload.claim ? 'Credit request claimed' : 'Credit request unclaimed',
      );
      return true;
    }
    handleToast(
      ToastType.Error,
      payload.claim
        ? 'Failed to claim credit request'
        : 'Failed to unclaim credit request',
    );
  } catch (error) {
    const e = createError(
      error,
      payload.claim
        ? 'Failed to claim credit request'
        : 'Failed to unclaim credit request',
    );
    handleToast(ToastType.Error, e.message);
  }
  return false;
};

export const fetchDoNotSolicitRequests = async () => {
  try {
    const response: V4ApiResponse<DoNotSolicitRequest[]> = await apiClient.get(
      '/Queue/DoNotSolicit',
    );
    return response;
  } catch (error) {
    const e = createError(error, 'Failed to get Do Not Solicit Requests');
    handleToast(ToastType.Error, e.message);
  }
};

export const fetchPendingCustomers = async () => {
  try {
    const response: V4ApiResponse<PendingCustomers[]> = await apiClient.get(
      'Customer/pendingcustomers',
    );
    return response.value;
  } catch (e: unknown) {
    const error = createError(e, 'Failed to get pending customers');
    handleToast(ToastType.Error, error.message);
  }
};

export const claimPendingCustomer = async (payload: {
  id: number;
  claim: boolean;
  comment?: string;
}): Promise<boolean> => {
  try {
    const response = (await apiClient.post(
      'Customer/pendingcustomers/claim',
      payload,
    )) as V4ApiResponse<unknown>;
    if (response.isSuccess) {
      handleToast(
        ToastType.Success,
        payload.claim
          ? 'Pending customer claimed'
          : 'Pending customer unclaimed',
      );
      return true;
    }
    handleToast(
      ToastType.Error,
      payload.claim
        ? 'Failed to claim pending customer'
        : 'Failed to unclaim pending customer',
    );
  } catch (error) {
    const e = createError(
      error,
      payload.claim
        ? 'Failed to claim pending customer'
        : 'Failed to unclaim pending customer',
    );
    handleToast(ToastType.Error, e.message);
  }
  return false;
};

export const changeQueueVisibility = async (id: number, visible: boolean) => {
  try {
    await apiClient.post(`customer/changequeuevisibility/${id}/${visible}`);
    handleToast(ToastType.Success, 'Prepaid Cleared');
  } catch (error) {
    if (error instanceof Error) {
      handleToast(ToastType.Error, 'Failed to clear prepaid');
    }
  }
};

export const fetchAllNotification = async () => {
  try {
    const response: V4ApiResponse<NotificationTypes[]> =
      await apiClient.get('/Notification');
    return response;
  } catch (e: unknown) {
    const error = createError(e, 'Failed to get Notification');
    handleToast(ToastType.Error, error.message);
  }
};

export const notificationMarkAsRead = async (payload: number[]) => {
  try {
    const response: V4ApiResponse<boolean> = await apiClient.post(
      '/Notification/MarkAsRead',
      payload,
    );
    return response;
  } catch (error) {
    const e = createError(error, 'Failed to mark as read');
    handleToast(ToastType.Error, e.message);
  }
};

export const fetchAllNeedPaperwork = async () => {
  try {
    const response: V4ApiResponse<NeedPaperworkTypes[]> = await apiClient.get(
      '/Queue/NeedPaperwork',
    );
    return response;
  } catch (error) {
    const e = createError(error, 'Failed to get Need Paperwork');
    handleToast(ToastType.Error, e.message);
  }
};

export const fetchAllPendingChecks = async () => {
  try {
    const response: V4ApiResponse<PendingChecksTypes[]> = await apiClient.get(
      '/Queue/PendingChecks',
    );
    return response;
  } catch (e: unknown) {
    const error = createError(e, 'Failed to get Pending Checks');
    handleToast(ToastType.Error, error.message);
  }
};

export const getAllCheckRequest = async (id: number) => {
  try {
    const response: V4ApiResponse<CheckRequestType> = await apiClient.get(
      `/Request/CheckRequest/${id}`,
    );
    return response;
  } catch (error) {
    const e = createError(error, 'Failed to get Check Request');
    handleToast(ToastType.Error, e.message);
  }
};

export const approveCheckRequest = async (id: number) => {
  try {
    const response: V4ApiResponse<boolean> = await apiClient.post(
      `/request/approvecheck/${id}`,
    );

    return {
      success: response.isSuccess && Boolean(response.value),
      message: response.errors[0]?.message ?? 'Error approving check request',
    };
  } catch (error) {
    const e =
      // @ts-expect-error Just being overly cautious here.
      'map' in error
        ? (error as { errorMessage: string; propertyName: string }[]).reduce(
            (acc, curr) => {
              acc.push(`${curr.propertyName}: ${curr.errorMessage}`);
              return acc;
            },
            [] as string[],
          )
        : error;

    throw createError(e, 'Error approving check request');
  }
};

export const denyCheckRequest = async (payload: {
  id: number;
  comment: string;
}) => {
  try {
    const response: V4ApiResponse<boolean> = await apiClient.post(
      '/Request/DenyCheck',
      payload,
    );
    return response;
  } catch (error) {
    const e = createError(error, 'Failed to deny Check Request');
    handleToast(ToastType.Error, e.message);
  }
};

export function styleQuoteLocator(payload: { quoteLocator: string }) {
  const { quoteLocator } = payload;
  if (quoteLocator.length === 10) {
    return `${quoteLocator.slice(0, 3)}-${quoteLocator.slice(
      3,
      6,
    )}-${quoteLocator.slice(6)}`;
  }

  return quoteLocator;
}

export const fetchAllApprovals = async () => {
  try {
    const response: V4ApiResponse<AllApprovalsTypes[]> = await apiClient.get(
      '/queue/pendingapprovalsanddisputes/',
    );
    return response;
  } catch (error) {
    const e = createError(error, 'Failed to get All Approvals');
    handleToast(ToastType.Error, e.message);
  }
};

export const fetchDisputedLoads = async () => {
  try {
    const response: V4ApiResponse<AllApprovalsTypes[]> = await apiClient.get(
      '/queue/disputedorders/',
    );
    return response;
  } catch (error) {
    const e = createError(error, 'Failed to get Disputed Loads');
    handleToast(ToastType.Error, e.message);
  }
};

export const findOrder = async (id: number) => {
  try {
    const response: V4ApiResponse<boolean> = await apiClient.get(
      `order/find?id=${id}`,
    );
    if (response.isSuccess) {
      return Boolean(response.value);
    }
  } catch (error) {
    handleToast(ToastType.Error, 'The page you requested is not found');
  }
};

export const checkUsersPostingValid = async () => {
  try {
    await apiClient.get('/posting/checkusertspostingvalid');
  } catch (error) {
    logError('checkUsersPostingValid', error as Error);
    if (
      Array.isArray(error) &&
      size(error) > 0 &&
      (error[0] as { errorMessage: string }).errorMessage
    ) {
      handleToast(
        ToastType.Error,
        (error[0] as { errorMessage: string }).errorMessage,
      );
    }
  }
};

export const batchCalculateMileage = async (payload: {
  locations: {
    address: {
      city: string;
      state: string;
    };
  }[];
}) => {
  try {
    const response: PcMilerApiResponse = await pcMilerClient.post(
      'locations/address/batch',
      payload,
    );
    return response;
  } catch (error) {
    handleToast(
      ToastType.Error,
      (
        error as {
          response: {
            data: {
              Description: string;
            }[];
          };
        }
      ).response.data
        .map((x) => x.Description)
        .join('\n') || 'Error calculating mileage',
    );
  }
};

export const calculateMilesForPricing = async (
  payload: string[],
  type: 'Practical' | 'Shortest',
) => {
  try {
    const response: Record<string, string | number>[] = await pcMilerClient.get(
      'route/routeReports',
      {
        params: {
          reports: 'CalcMiles',
          stops: payload.join(';'),
          routeType: type,
          dataVersion: '',
        },
      },
    );
    return response;
  } catch (error) {
    handleToast(
      ToastType.Error,
      (
        error as {
          response: {
            data: {
              Description: string;
            }[];
          };
        }
      ).response.data
        .map((x) => x.Description)
        .join('\n') || 'Error getting mileage',
    );
  }
};

const handlePostingError = (error: unknown, toastMessage: string) => {
  if (Array.isArray(error)) {
    const errorMessages = error.map((v) => {
      if (typeof v === 'string') {
        return v;
      } else if (typeof v === 'object' && v !== null && 'errorMessage' in v) {
        return (v as { errorMessage: string }).errorMessage;
      }
    });
    handleToast(ToastType.Error, errorMessages.join(', '));
  } else {
    handleToast(ToastType.Error, toastMessage);
  }
};

export const fetchAvailablePostings = async () => {
  try {
    const response: V4ApiResponse<AvailablePosting[]> =
      await apiClient.get('/posting');
    if ('value' in response) {
      return response.value;
    }
  } catch (error) {
    handlePostingError(error, 'Failed to get available postings');
  }
};

export const refreshPosting = async (postingId: number) => {
  try {
    const response: V4ApiResponse<AvailablePosting> = await apiClient.get(
      `posting/refresh/${postingId}`,
    );

    if ('data' in response) {
      return response.data as AvailablePosting;
    }
  } catch (error) {
    throw createError(error, 'Failed to refresh Posting');
  }
};

export const fetchAllPendingApprovals = (): Promise<
  PendingApprovals[] | undefined
> => {
  const db = getDB();
  if (!db) return Promise.resolve(undefined);

  return fetchWithCache<PendingApprovals[]>({
    dbStore: db.pendingApprovals,
    cacheKey: 'pending-approvals',
    apiCall: async () => {
      try {
        const response: V4ApiResponse<PendingApprovals[]> = await apiClient.get(
          '/Queue/PendingApprovals?old=true',
        );
        if ('value' in response) {
          return response.value;
        }
        return [];
      } catch (error) {
        const e = createError(error, 'Unable to fetch Pending Approvals data');
        handleToast(ToastType.Error, e.message);
        return [];
      }
    },
    duration: CACHE_DURATION.ONE_DAY,
  });
};

export const fetchBillingholds = (): Promise<HoldsInfo[] | undefined> => {
  const db = getDB();
  if (!db) return Promise.resolve(undefined);

  return fetchWithCache<HoldsInfo[]>({
    dbStore: db.billingHolds,
    cacheKey: 'billing-holds',
    apiCall: async () => {
      try {
        const response: V4ApiResponse<HoldsInfo[]> =
          await apiClient.get('queue/BillingHolds');
        if ('value' in response) {
          return response.value;
        }
        return [];
      } catch (error) {
        const e = createError(error, 'Unable to fetch billing holds data');
        handleToast(ToastType.Error, e.message);
        return [];
      }
    },
    duration: CACHE_DURATION.ONE_DAY,
  });
};

export const fetchOldLoadsWarningData = (
  payload: OldLoadPayload,
): Promise<
  | {
      data: OldLoad[];
      totalCount: number;
    }
  | undefined
> => {
  const db = getDB();
  if (!db) return Promise.resolve(undefined);

  return fetchWithCache<{
    data: OldLoad[];
    totalCount: number;
  }>({
    dbStore: db.oldLoads,
    cacheKey: 'old-loads',
    apiCall: async () => {
      try {
        const response: V4ApiResponse<{
          data: OldLoad[];
          totalCount: number;
        }> = await apiClient.post('/queue/oldloads', payload);
        if ('value' in response) {
          return response.value;
        }
        return { data: [], totalCount: 0 };
      } catch (error) {
        const e = createError(error, 'Unable to fetch old load data');
        handleToast(ToastType.Error, e.message);
        return { data: [], totalCount: 0 };
      }
    },
    duration: CACHE_DURATION.ONE_DAY,
  });
};

export const addToCallingQueue = async (id: number, comment: string) => {
  try {
    const response: {
      isSuccess: boolean;
      message: string;
    } = await apiClient.post('/Load/addtocallingqueue', {
      id,
      comment,
    });
    return response;
  } catch (error) {
    const e = createError(error, 'Unable to add to calling queue');
    handleToast(ToastType.Error, e.message);
  }
};

export const addToBillingQueue = async (id: number) => {
  try {
    const response: {
      isSuccess: boolean;
      message: string;
    } = await apiClient.post(`/Order/addtobillingqueue/${id}`);
    return response;
  } catch (error) {
    const e = createError(error, 'Unable to add to billing queue');
    handleToast(ToastType.Error, e.message);
  }
};

export const getLatestReleaseNotesApi = (): Promise<
  ReleaseNoteType | undefined
> => {
  const querykey = 'release-notification-latest';
  return fetchWithCache<ReleaseNoteType>({
    dbStore: getDB()!.releaseNotesLatest,
    cacheKey: querykey,
    apiCall: async () => {
      try {
        const response: V4ApiResponse<ReleaseNoteType> = await apiClient.get(
          'ReleaseNotes/latest',
        );
        if ('value' in response) {
          return response.value;
        }
        return {} as ReleaseNoteType;
      } catch (e: unknown) {
        return {} as ReleaseNoteType;
      }
    },
    duration: CACHE_DURATION.TWO_HOUR,
  });
};
